import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  FINDING_SCOPE_DEPENDENCIES,
  SCOPE_GROUP_DEPENDENCIES,
  type ScopeDependencies,
  type ScopeDependenciesQueryResponse,
  type ScopeGroupDependenciesQueryResponse,
} from "./ScopeGuardrailsApi";
import type { ScopeNodeType } from "./types";

export interface CheckScopeDependenciesParams {
  id: string;
  nodeType: ScopeNodeType;
  includeDescendants?: boolean;
}

// A failed check is its own branch, so a caller can never read it as "no
// dependents". A check superseded by a newer one resolves `stale`.
export type CheckScopeDependenciesResult =
  | { status: "ok"; dependencies: ScopeDependencies }
  | { status: "error"; error: unknown }
  | { status: "stale" };

export interface UseScopeDependenciesReturnValue {
  check: (
    params: CheckScopeDependenciesParams
  ) => Promise<CheckScopeDependenciesResult>;
  loading: boolean;
  called: boolean;
  dependencies: ScopeDependencies | null;
  error?: unknown;
}

interface State {
  called: boolean;
  loading: boolean;
  dependencies: ScopeDependencies | null;
  error?: unknown;
}

export const useScopeDependencies = (): UseScopeDependenciesReturnValue => {
  const [runScope] = useLazyQuery<ScopeDependenciesQueryResponse>(
    FINDING_SCOPE_DEPENDENCIES,
    { fetchPolicy: "network-only" }
  );
  const [runGroup] = useLazyQuery<ScopeGroupDependenciesQueryResponse>(
    SCOPE_GROUP_DEPENDENCIES,
    { fetchPolicy: "network-only" }
  );
  // Only the most recent check may update what the hook surfaces.
  const latest = useRef(0);
  const [state, setState] = useState<State>({
    called: false,
    loading: false,
    dependencies: null,
  });

  const check = useCallback(
    async ({
      id,
      nodeType,
      includeDescendants = false,
    }: CheckScopeDependenciesParams): Promise<CheckScopeDependenciesResult> => {
      const seq = ++latest.current;
      setState((current) => ({
        ...current,
        called: true,
        loading: true,
        error: undefined,
      }));
      try {
        let dependencies: ScopeDependencies | null | undefined;
        let queryError: unknown;
        if (nodeType === "group") {
          const res = await runGroup({ variables: { id, includeDescendants } });
          queryError = res.error;
          dependencies = res.data?.scope_group_dependencies;
        } else {
          const res = await runScope({ variables: { id } });
          queryError = res.error;
          dependencies = res.data?.finding_scope_dependencies;
        }
        if (seq !== latest.current) {
          return { status: "stale" };
        }
        if (queryError) {
          throw queryError;
        }
        if (!dependencies) {
          throw new Error("Scope dependencies: empty response");
        }
        setState({ called: true, loading: false, dependencies });
        return { status: "ok", dependencies };
      } catch (error) {
        if (seq !== latest.current) {
          return { status: "stale" };
        }
        setState({ called: true, loading: false, dependencies: null, error });
        return { status: "error", error };
      }
    },
    [runScope, runGroup]
  );

  return { check, ...state };
};
